import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Grid, TextField, Button, Select, MenuItem, InputLabel, FormControl, FormHelperText, Typography } from '@mui/material';
import FormPage from '../components/FormPage';
import { updateCustomerSupportComplaint, fetchCustomerSupportComplaint, fetchCustomers, fetchVehicles } from '../services/ApiService';

const CATEGORIES = ["NR", "Camera", "SD Card", "GPS", "Other"];

export function EditCustomerComplaint() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [haulierOptions, setHaulierOptions] = React.useState({});
  const [trucks, setTrucks] = React.useState({});
  const [record, setRecord] = React.useState(location.state?.record || null);
  const [formData, setFormData] = React.useState(null);
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchCustomers().then((res) => {
      setHaulierOptions(res.data.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}));
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!record) {
      fetchCustomerSupportComplaint(id).then((res) => setRecord(res.data)).catch(() => navigate('/customer-support', { state: { tab: 1 } }));
    }
  }, [record, id]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (record) {
      setFormData({
        complain_number: record.complain_number || "",
        logged_time: record.logged_time || "",
        customer: record.customer || "",
        vehicle: record.vehicle || "",
        complainant: record.complainant || "",
        category: record.category || "",
        details: record.details || "",
        status: record.status || "",
        technical_department_remarks: record.technical_department_remarks || "",
        resolution_time: record.resolution_time || "",
      });
    }
  }, [record]);

  React.useEffect(() => {
    if (formData?.customer) {
      fetchVehicles(formData.customer).then((res) => {
        setTrucks(res.data.reduce((acc, v) => ({ ...acc, [v.id]: v.registration_number }), {}));
      }).catch(() => {});
    }
  }, [formData?.customer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCancel = () => navigate('/customer-support', { state: { tab: 1 } });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataObj = new FormData();
    for (const key in formData) {
      formDataObj.append(key, formData[key]);
    }
    try {
      await updateCustomerSupportComplaint(id, formDataObj);
      navigate('/customer-support', { state: { tab: 1, successMessage: 'Customer Support Complain updated successfully' } });
    } catch (error) {
      const errData = error.response?.data;
      if (errData) {
        const newErrors = {};
        for (const field in errData) {
          newErrors[field] = Array.isArray(errData[field]) ? errData[field].join(", ") : errData[field];
        }
        setFormErrors(newErrors);
      } else {
        setFormErrors({ general: "An unexpected error occurred." });
      }
    }
  };

  if (!formData) {
    return (
      <FormPage title="Edit Customer Support Complain" backTo="/customer-support">
        <Typography>Loading...</Typography>
      </FormPage>
    );
  }

  return (
    <FormPage
      title="Edit Customer Support Complain"
      backTo="/customer-support"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Update</Button>
      </>}
    >
      <Grid container spacing={2}>
        {[
          { label: "Customer", name: "customer", options: haulierOptions },
          { label: "Vehicle Number", name: "vehicle", options: trucks },
        ].map(({ label, name, options }) => (
          <Grid item xs={12} sm={6} key={name}>
            <FormControl fullWidth required error={!!formErrors[name]}>
              <InputLabel>{label}</InputLabel>
              <Select name={name} value={formData[name] || ""} onChange={handleChange} label={label}>
                {Object.entries(options).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
              {formErrors[name] && <FormHelperText>{formErrors[name]}</FormHelperText>}
            </FormControl>
          </Grid>
        ))}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required label="Complainant" name="complainant" placeholder="Name of person who complained"
            value={formData.complainant} onChange={handleChange}
            error={Boolean(formErrors.complainant)} helperText={formErrors.complainant}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={Boolean(formErrors.category)}>
            <InputLabel>Category</InputLabel>
            <Select name="category" value={formData.category || ""} onChange={handleChange} label="Category">
              {CATEGORIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
            {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth label="Details" name="details" placeholder="Describe the complaint in detail"
            value={formData.details} onChange={handleChange} multiline rows={3}
            error={Boolean(formErrors.details)} helperText={formErrors.details}
          />
        </Grid>
      </Grid>
    </FormPage>
  );
}
